import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CirclePlus, MoreHorizontal } from "lucide-react";
import Pagination from "@/components/pagination/Pagination";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TUsersList } from "@/api/DashboardTypes";
import axios from "axios";
import { toast } from "react-toastify";
import { usePatientContext } from "@/hooks/usePatientContext";

interface TablePatientsListProps {
  patientsList: TUsersList[];
  setPatientsList: React.Dispatch<React.SetStateAction<TUsersList[]>>;
}


const TablePatientsList: React.FC<TablePatientsListProps> = ({
  patientsList,
  setPatientsList,
}) => {
  const navigate = useNavigate();
  const { setPatientInfo } = usePatientContext();

  const handleRowClick = (
    patientId: number,
    firstName: string,
    lastName: string
  ) => {
    setPatientInfo({ firstName, lastName, patientId });
    navigate(`/praticien/${patientId}/dossier`);
  };

  const [currentTab, setCurrentTab] = useState("actifs");
  const [activePage, setActivePage] = useState(1);
  const [inactivePage, setInactivePage] = useState(1);

  const sortedPatientsList = [...patientsList].sort((a, b) =>
    a.lastName.localeCompare(b.lastName)
  );

  const activePatients = sortedPatientsList.filter(
    (patient) => patient.accountStatus === "ACTIF"
  );
  const inactivePatients = sortedPatientsList.filter(
    (patient) => patient.accountStatus !== "ACTIF"
  );

  const patientsPerPage = 7;

  const getCurrentPatients = () => {
    if (currentTab === "actifs") {
      const start = (activePage - 1) * patientsPerPage;
      return activePatients.slice(start, start + patientsPerPage);
    } else {
      const start = (inactivePage - 1) * patientsPerPage;
      return inactivePatients.slice(start, start + patientsPerPage);
    }
  };

  const totalPages =
    currentTab === "actifs"
      ? Math.ceil(activePatients.length / patientsPerPage)
      : Math.ceil(inactivePatients.length / patientsPerPage);

  const DeleteUser = (id: number, email: string) => {
    try {
      axios
        .delete(`/practitioner/patient/` + id)
        .then(() => {
          toast.success(
            `Le compte avec l'email : ${email} a bien été supprimé`,
            {
              position: "top-center",
              autoClose: 2000,
            }
          );
        })
        .catch((error) => {
          console.error("Error response:", error.response);
          console.error("Erreur lors de la suppression du patient:", error);
          toast.error(
            `Erreur lors de la suppression du compte avec l'email : ${email}`,
            {
              position: "top-center",
              autoClose: 2000,
            }
          );
        });
      const patientsListsAfterDelete = patientsList.filter(
        (patient) => patient.id !== id
      );
      setPatientsList(patientsListsAfterDelete);
    } catch (error) {
      console.error("Erreur lors de la suppression du patient:", error);
      toast.error(
        `Erreur lors de la suppression du compte avec l'email : ${email}`,
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
    }
  };

  return (
    <Card className="w-5/6 h-5/6">
      <CardHeader className="flex flex-row items-center justify-between">
        {/* Bouton Nouveau Patient */}
        <div className="w-1/4 flex justify-center align-middle">
          <Button
            variant="outline"
            onClick={() => navigate(`/praticien/nouveau`)}
          >
            <CirclePlus className="mr-2" /> Nouveau Patient
          </Button>
        </div>

        {/* Titre et tabs côte à côte */}
        <div className="flex flex-col gap-2 items-center w-2/4">
          <div className="flex flex-row items-center justify-center gap-4">
            <CardTitle>Mes patients</CardTitle>
          </div>
          <CardDescription>Liste de mes patients</CardDescription>
        </div>
        <div className="w-1/4 flex justify-center align-middle">
          {" "}
          {/* TabsList intégré à côté du titre */}
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList className="border-none">
              <TabsTrigger value="actifs">Comptes actifs</TabsTrigger>
              <TabsTrigger value="inactifs">Comptes inactifs</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>

      <CardContent className="pb-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Prénom</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Email</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {getCurrentPatients().map((patient) => (
              <TableRow
                key={patient.id}
                className="cursor-pointer hover:bg-gray-100"
              >
                <TableCell
                  onClick={() =>
                    handleRowClick(
                      patient.id,
                      patient.firstName,
                      patient.lastName
                    )
                  }
                >
                  {patient.lastName}
                </TableCell>
                <TableCell
                  onClick={() =>
                    handleRowClick(
                      patient.id,
                      patient.firstName,
                      patient.lastName
                    )
                  }
                >
                  {patient.firstName}
                </TableCell>
                <TableCell
                  onClick={() =>
                    handleRowClick(
                      patient.id,
                      patient.firstName,
                      patient.lastName
                    )
                  }
                >
                  {patient.accountStatus}
                </TableCell>
                <TableCell
                  onClick={() =>
                    handleRowClick(
                      patient.id,
                      patient.firstName,
                      patient.lastName
                    )
                  }
                >
                  {patient.email}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem
                        onSelect={async (e) => {
                          e.preventDefault();
                          const newStatus =
                            patient.accountStatus === "ACTIF"
                              ? "INACTIF"
                              : "ACTIF";
                          try {
                            await axios.put(
                              `/practitioner/patient/${patient.id}`,
                              { accountStatus: newStatus }
                            );
                            toast.success(
                              `Compte rendu ${
                                newStatus === "ACTIF" ? "actif" : "inactif"
                              } avec succès`
                            );
                            // Mets à jour localement la liste pour refléter le changement instantanément
                            setPatientsList((prev: TUsersList[]) =>
                              prev.map((p: TUsersList) =>
                                p.id === patient.id
                                  ? { ...p, accountStatus: newStatus as TUsersList['accountStatus'] }
                                  : p
                              )
                            );
                          } catch (err) {
                            toast.error("Erreur lors du changement de statut");
                          }
                        }}
                      >
                        {patient.accountStatus === "ACTIF"
                          ? "Rendre inactif"
                          : "Rendre actif"}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                      <Dialog>
                        <DialogTrigger onSelect={(e) => e.preventDefault()}>
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            Effacer de la base de données
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>
                              Êtes-vous absolument sûr ?
                            </DialogTitle>
                            <DialogDescription>
                              Cette action ne peut pas être annulée. Cela
                              supprimera définitivement le compte et effacera
                              les données de nos serveurs.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="flex justify-center">
                            <DialogClose asChild>
                              <Button
                                variant={"default"}
                                className="w-[240px]"
                                onClick={() =>
                                  DeleteUser(patient.id, patient.email)
                                }
                              >
                                Confirmer
                              </Button>
                            </DialogClose>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <CardFooter className="flex justify-center pb-2">
        <Pagination
          currentPage={currentTab === "actifs" ? activePage : inactivePage}
          totalPages={totalPages}
          onPageChange={
            currentTab === "actifs" ? setActivePage : setInactivePage
          }
        />
      </CardFooter>
    </Card>
  );
};

export default TablePatientsList;
